import { motion } from 'framer-motion';
import { type ReactNode } from 'react';

/**
 * Stylised accreditation badges for the security section.
 *
 * Drawn in code (SVG) rather than using trademarked third-party marks:
 * - ISO 27001, ISO/IEC 42001 and Cyber Essentials Plus are target standards, not credentials
 *   currently held by the platform.
 * - ICAEW, IPA and R3 are professional/industry bodies; The Insolvency Service
 *   is a government agency. Their marks aren't ours to display without
 *   permission on a public surface.
 *
 * Each badge has a distinct visual identity (icon + colour) that nods to
 * the credential's visible role without imitating the official mark. A
 * The caption above the strip makes the contextual, forward-looking posture
 * explicit, in line with §2.2 (never misrepresent regulatory status).
 *
 * When real logo assets and permissions are in place, swap each badge's
 * <svg> for an <img> from /public/brand/accreditations/.
 */

interface AccreditationBadge {
  id: string;
  name: string;
  subtitle: string;
  secondary?: { name: string; subtitle: string };
  /** Tailwind/CSS hex for the badge accent (icon stroke + thin top edge). */
  accent: string;
  icon: ReactNode;
}

const BADGES: AccreditationBadge[] = [
  {
    id: 'insolvency-service',
    name: 'The Insolvency Service',
    subtitle: 'Government agency',
    accent: '#6FADD2',
    icon: <IconGovernment />,
  },
  {
    id: 'icaew',
    name: 'ICAEW',
    subtitle: 'Insolvency regulator',
    accent: '#14B8A6',
    icon: <IconScales />,
  },
  {
    id: 'ipa',
    name: 'IPA',
    subtitle: 'Professional insolvency body',
    accent: '#6366F1',
    icon: <IconScales />,
  },
  {
    id: 'r3',
    name: 'R3',
    subtitle: 'Restructuring & insolvency trade body',
    accent: '#EF4444',
    icon: <IconRecovery />,
  },
  {
    id: 'uk-gdpr',
    name: 'UK GDPR',
    subtitle: 'Data protection',
    accent: '#F59E0B',
    icon: <IconDocument />,
  },
  {
    id: 'iso27001',
    name: 'ISO 27001',
    subtitle: 'Information security',
    secondary: { name: 'ISO/IEC 42001', subtitle: 'AI management systems' },
    accent: '#3B82F6',
    icon: <IconShield />,
  },
  {
    id: 'cyber-essentials-plus',
    name: 'Cyber Essentials Plus',
    subtitle: 'UK cyber security scheme',
    accent: '#10B981',
    icon: <IconCheck />,
  },
];

export function AccreditationBadges() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className="mt-6 flex flex-col gap-5"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-ink-dim">
          Regulatory &amp; security landscape
        </span>
        <span className="hidden text-[11px] font-medium uppercase tracking-[0.18em] text-ink-dim sm:inline">
          Context only · no endorsement or certification implied
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
        {BADGES.map((badge, i) => (
          <BadgeCard key={badge.id} badge={badge} index={i} />
        ))}
      </div>
    </motion.div>
  );
}

function BadgeCard({
  badge,
  index,
}: {
  badge: AccreditationBadge;
  index: number;
}) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      whileHover={{ y: -3 }}
      transition={{
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1],
        delay: 0.05 + index * 0.05,
      }}
      className="pitch-card pitch-card-interactive group relative flex flex-col items-start gap-4 overflow-hidden rounded-xl p-5"
    >
      {/* Top accent edge — a 2px brand-coloured strip that ties the badge
          to its credential family. */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-[2px] opacity-90"
        style={{ background: badge.accent }}
      />
      {/* Soft corner glow in the badge's accent colour. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full opacity-[0.10] blur-2xl transition-opacity group-hover:opacity-[0.18]"
        style={{ background: badge.accent }}
      />

      <span
        className="relative flex h-10 w-10 flex-none items-center justify-center rounded-lg border"
        style={{
          borderColor: `${badge.accent}66`,
          background: `${badge.accent}14`,
          color: badge.accent,
        }}
      >
        {badge.icon}
      </span>

      <div className="relative flex flex-col gap-1">
        <span className="text-[14px] font-semibold uppercase tracking-[0.12em] text-ink-strong">
          {badge.name}
        </span>
        <span className="text-[11px] leading-snug text-ink-muted">
          {badge.subtitle}
        </span>
        {badge.secondary ? (
          <div className="mt-2 flex flex-col gap-1 border-t border-border-subtle pt-3">
            <span className="text-[14px] font-semibold uppercase tracking-[0.12em] text-ink-strong">
              {badge.secondary.name}
            </span>
            <span className="text-[11px] leading-snug text-ink-muted">
              {badge.secondary.subtitle}
            </span>
          </div>
        ) : null}
      </div>
    </motion.article>
  );
}

/* ─── Icons ────────────────────────────────────────────────────────────── */

const ICON_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  className: 'h-5 w-5',
};

function IconShield() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M12 3l8 3v6c0 4.5-3.5 8.2-8 9-4.5-.8-8-4.5-8-9V6l8-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function IconCheck() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12l3 3 5-6" />
    </svg>
  );
}

function IconDocument() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M14 3v6h6" />
      <path d="M8 13h8M8 17h5" />
    </svg>
  );
}

function IconRecovery() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M21 12a9 9 0 1 1-3.5-7.1" />
      <path d="M21 4v5h-5" />
    </svg>
  );
}

function IconScales() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M12 4v16" />
      <path d="M6 20h12" />
      <path d="M6 8l-3 6a3 3 0 0 0 6 0L6 8zM18 8l-3 6a3 3 0 0 0 6 0L18 8z" />
      <path d="M4 6h16" />
    </svg>
  );
}

function IconGovernment() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M4 10h16" />
      <path d="M5 10l7-5 7 5" />
      <path d="M6 10v8M10 10v8M14 10v8M18 10v8" />
      <path d="M4 18h16" />
    </svg>
  );
}
